import { useEffect, useRef, useState, type ReactNode } from "react";
import { useCustomerDataAdapter } from "@shared-core/data/CustomerDataAdapterContext";
import type { CustomerOrderWithItems } from "@shared-core/data/customer-api-types";
import {
  formatDateOrDash,
  formatQuotedPrice,
  formatWeekdayDateOrDash,
} from "@shared-core/domain/formatters";
import {
  carriersByItem,
  isItemDelivered,
  itemListStatus,
  itemRef,
  orderNeededBy,
  orderTitle,
  pence,
  pressingConsignmentSentence,
  withConsignmentProgress,
} from "@shared-core/domain/customerOrderWithItems";
import {
  parcelEventsState,
  useOrderConsignmentProgress,
} from "@shared-core/domain/useOrderConsignmentProgress";
import { Badge } from "../../../app/components/primitives/badge";
import { Button } from "../../../app/components/primitives/button";
import { Card } from "../../../app/components/primitives/card";
import { cn } from "../../../app/components/primitives/utils";
import { SummaryPageFrame } from "../../customer-pages/SummaryPageFrame";
import { CalendarClock } from "lucide-react";
import { DeliveryByAddress } from "../../customer-pages/DeliveryByAddress";
import { DocumentsList } from "../../customer-pages/DocumentsList";
import { ItemsListDetail } from "../../customer-pages/ItemsListDetail";
import { NotesPanel } from "../../customer-pages/NotesPanel";
import {
  NeedsYouList,
  type NeedsYouItem,
} from "../../customer-pages/NeedsYouList";
import {
  OrderSummaryBar,
  OrderSummaryPanel,
} from "../../customer-pages/OrderSummaryPanel";
import {
  PageTabs,
  resolvePageTab,
  type PageTab,
} from "../../customer-pages/PageTabs";
import { PriceBreakdown } from "../../customer-pages/PriceBreakdown";
import { useItemPageParams } from "../../customer-pages/useItemPageParams";
import {
  SUMMARY_BESIDE_PAGE,
  useMediaQuery,
} from "../../customer-pages/useMediaQuery";
import { usePortalViewer } from "../PortalViewerContext";
import { POLLARDS_PHONE } from "../portalContact";
import { OrderParcelEvents } from "./OrderParcelEvents";
import {
  OrderItemPane,
  resolveItemPane,
  type ItemPaneId,
} from "./OrderItemPane";

// The order page of a Tharstern-era order, one item or many, in the shape of the signed-in CIS order page
// (`OrderPageBody`, Brad 9 Oct): the header, then Needs you · N, Items · N (the list beside the chosen item's
// detail, or, for one item, that detail straight away), Delivery, Price, Documents, Notes, with the money summary
// beside the page (a bar and sheet on a phone). Tab, item and pane live in the URL (`?tab=&item=&view=`), so an
// item can be linked to.
//
// What a Tharstern order has that a CIS order does not, and the reverse:
//   - Needs you is the read's `awaiting_customer` sentence per item. A proof cannot be decided here (a job's proofs
//     went with AW5), so a row opens the item and offers no Review button.
//   - Nothing is payable here: the summary shows the totals and where invoicing stands, never a Pay button.
//   - No proof history, versions or artwork uploads exist for these items; an item shows what we hold (the artwork
//     file name once the artwork modules serve it).
//   - Delivery is one place, the tab (P05): one block per address with each item's row, and one per consignment with
//     when it arrives or did and its milestones, each consignment's progress its own lazy read. The item's details
//     link there.
// Every sentence is the read's or counts and joins what the read gave; the body places them.

/** The Delivery tab's groups for an item with no address to link to: a collection and an address still to come. */
const NO_ADDRESS_GROUPS: ReadonlySet<string> = new Set([
  "collection",
  "unknown",
]);

export function OrderWithItemsBody({
  order,
  customerName,
  backLink,
}: {
  order: CustomerOrderWithItems;
  customerName: string | null;
  /** The shell's way back (to its Orders list), set above the order number in the page's header. */
  backLink?: ReactNode;
}) {
  const { searchParams, setParams, selectItem } = useItemPageParams();

  const items = order.items;
  const numbered = items.map((item, index) => ({
    item,
    number: itemRef(index),
  }));
  const needs = numbered.filter(({ item }) => item.awaiting_customer);
  const hasAttention = (orderRef: string) =>
    needs.some(({ item }) => item.order_ref === orderRef);

  // The tab a link with none lands on is fixed when the order loads: Needs you when anything is outstanding,
  // else Items.
  const [landingTab] = useState<PageTab>(() =>
    resolvePageTab(null, needs.length),
  );
  const requestedTab = searchParams.get("tab");
  const tab = requestedTab ? resolvePageTab(requestedTab, 0) : landingTab;
  const itemKey = searchParams.get("item");
  const summaryPanelRef = useRef<HTMLElement>(null);

  // A link to a later item of a many-item order (the address of its old own page) goes straight to that item; the
  // address of the first item is the order's own, and lands as the order does.
  const askedRef = order.order_ref;
  useEffect(() => {
    if (
      searchParams.get("tab") === null &&
      itemKey === null &&
      items.length > 1 &&
      items[0].order_ref !== askedRef &&
      items.some((i) => i.order_ref === askedRef)
    ) {
      setParams({ tab: "items", item: askedRef });
    }
    // Once, when the order is shown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultKey =
    needs[0]?.item.order_ref ??
    (items.some((i) => i.order_ref === askedRef)
      ? askedRef
      : items[0].order_ref);
  const pane = searchParams.get("view");
  // Each despatched consignment's progress is its own lazy read, made once when the order is shown (a slow carrier
  // never holds up the order). The carrier's word then moves the items' lists, next steps and Complete, and the
  // Delivery tab's blocks say when each parcel arrives or did.
  const adapter = useCustomerDataAdapter();
  const { viewKey } = usePortalViewer();
  const consignmentKeys = order.delivery_groups.flatMap((group) =>
    group.consignments.flatMap((c) => (c.key ? [c.key] : [])),
  );
  const consignmentProgress = useOrderConsignmentProgress(
    adapter,
    order.order_ref,
    consignmentKeys,
    viewKey,
  );
  const carriers = carriersByItem(order, consignmentProgress);
  const deliveryGroups = withConsignmentProgress(
    order.delivery_groups,
    consignmentProgress,
  );
  // The most pressing parcel's sentence is the header's once the carrier has spoken; until then, and for a parcel it
  // says nothing of, what the server knows from Tharstern (the order's banner).
  const banner =
    pressingConsignmentSentence(deliveryGroups) ?? order.banner ?? null;
  const neededBy = orderNeededBy(order);
  // Closed once every item has arrived (delivered, collected, supplied): a despatched one has only left us.
  const isClosed = items.every((item) => isItemDelivered(item, carriers));

  const isSummaryBesidePage = useMediaQuery(SUMMARY_BESIDE_PAGE);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const openItem = (orderRef: string) =>
    setParams({ tab: "items", item: orderRef, view: null });

  const needsRows: NeedsYouItem[] = needs.map(({ item, number }) => ({
    key: item.order_ref,
    icon: "proof",
    title: `${number} ${item.description}`,
    why: item.awaiting_customer ?? "",
    action: (
      <Button
        size="sm"
        variant="outline"
        onClick={() => openItem(item.order_ref)}
      >
        Open ›
      </Button>
    ),
  }));

  const first = items[0];
  // The number first, as the CIS order page has it; a job with no sales order has only its job number.
  const eyebrow = order.order_no ?? (first.job_no ? `Job ${first.job_no}` : "");
  // Tharstern gives an order no title of its own: a short one made of its items' names (orderTitle).
  const title = orderTitle(items);
  // The server words it ("Sam Price for Priya Shah", "You", "Our team, for you"): the page prints it.
  const placedBy = order.placed_by;

  const invoices = order.invoices ?? [];
  const hasVat = order.gross_amount != null;
  const invoiceState = order.invoice_state;
  const summaryProps = {
    label: "Your total and paying",
    sheetTitle: "Your total and paying",
    totalPence: pence(order.net_amount),
    totalIncVatPence: hasVat ? pence(order.gross_amount) : null,
    state: { text: invoiceState, isOwed: false },
    onOpenPrice: () => setParams({ tab: "price", item: null, view: null }),
    children: (
      <div className="space-y-2 text-sm">
        {invoices.map((invoice) => (
          <p
            key={invoice.invoice_no}
            className="flex flex-wrap justify-between gap-x-3 text-muted-foreground tabular-nums"
          >
            <span>
              {invoice.is_credit_note ? "Credit note" : "Invoice"}{" "}
              <span className="font-medium text-foreground">
                {invoice.invoice_no}
              </span>
              <span className="block text-xs">
                {formatDateOrDash(invoice.invoiced_on)}
                {invoice.due_on
                  ? ` · due ${formatDateOrDash(invoice.due_on)}`
                  : ""}
              </span>
            </span>
            <span>{formatQuotedPrice(pence(invoice.gross_amount))}</span>
          </p>
        ))}
        {invoices.length > 0 && (
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={() =>
              setParams({ tab: "documents", item: null, view: null })
            }
          >
            See your invoices ›
          </Button>
        )}
      </div>
    ),
  };

  return (
    <SummaryPageFrame
      hasSummary
      isSummaryBesidePage={isSummaryBesidePage}
      header={
        <>
          {backLink}
          <div className="space-y-2">
            {eyebrow && (
              <p className="font-mono text-xs text-muted-foreground">
                {eyebrow}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight break-words sm:text-3xl">
                {title}
              </h1>
              {isClosed && !items.every((i) => i.is_call_off) && (
                <Badge variant="success">Complete</Badge>
              )}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              {customerName && (
                <span>
                  For{" "}
                  <span className="font-medium text-foreground">
                    {customerName}
                  </span>
                </span>
              )}
              {hasVat && (
                <span>
                  {formatQuotedPrice(pence(order.gross_amount))} inc VAT ·{" "}
                  <span className="font-medium text-foreground">
                    {invoiceState}
                  </span>
                </span>
              )}
              <span>Ordered {formatWeekdayDateOrDash(order.ordered_at)}</span>
              {neededBy && (
                <span>Needed by {formatWeekdayDateOrDash(neededBy)}</span>
              )}
              {order.customer_reference && (
                <span>Your reference {order.customer_reference}</span>
              )}
              {placedBy && <span>Placed by {placedBy}</span>}
            </div>
            {banner && !isClosed && (
              <p
                className={cn(
                  "inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm font-medium",
                  banner.tone === "late"
                    ? "bg-red-50 text-red-900 dark:bg-red-950/30 dark:text-red-200"
                    : "bg-muted/60",
                )}
              >
                <CalendarClock className="size-4" />
                {banner.text}
              </p>
            )}
          </div>
        </>
      }
      panel={<OrderSummaryPanel {...summaryProps} panelRef={summaryPanelRef} />}
      bar={
        <OrderSummaryBar
          {...summaryProps}
          isSheetOpen={isSheetOpen}
          onSheetOpenChange={setIsSheetOpen}
          barAction={null}
        />
      }
    >
      <PageTabs
        label="Order"
        current={tab}
        needsCount={needs.length}
        itemsCount={items.length}
        itemsNeedAttention={needs.length > 0}
        onSelect={(t: PageTab) => setParams({ tab: t, item: null, view: null })}
      />

      <div role="tabpanel">
        {tab === "needs" && (
          <NeedsYouList
            rows={needsRows}
            emptySentence="Nothing needed from you right now."
          />
        )}
        {tab === "items" && (
          <ItemsListDetail
            items={numbered.map(({ item, number }) => ({
              key: item.order_ref,
              ref: number,
              title: item.description,
              status: itemListStatus(item, carriers),
              hasAttention: hasAttention(item.order_ref),
            }))}
            selectedKey={itemKey}
            defaultKey={defaultKey}
            onSelect={(key) => selectItem(key, { view: null })}
            backLabel="All items"
            renderDetail={(key) => {
              const entry = numbered.find((n) => n.item.order_ref === key);
              if (!entry) return null;
              return (
                <OrderItemPane
                  key={key}
                  item={entry.item}
                  itemNumber={entry.number}
                  pane={resolveItemPane(pane, entry.item)}
                  onPane={(p: ItemPaneId) => setParams({ view: p })}
                  carrier={carriers[entry.item.order_ref] ?? null}
                  deliveryAddresses={deliveryGroups
                    .filter(
                      (g) =>
                        g.rows.some((r) => r.ref === entry.number) &&
                        !NO_ADDRESS_GROUPS.has(g.key),
                    )
                    .map((g) => g.address_name)}
                  onOpenDelivery={() =>
                    setParams({ tab: "delivery", item: null, view: null })
                  }
                  orderedAt={order.ordered_at}
                  orderFacts={[
                    { label: "Order number", value: order.order_no },
                    { label: "Site", value: order.site_name },
                    { label: "Placed by", value: placedBy },
                  ]}
                />
              );
            }}
          />
        )}
        {tab === "delivery" && (
          <DeliveryByAddress
            groups={deliveryGroups}
            sentence={isClosed ? null : (banner?.text ?? null)}
            renderProgress={(consignment) => (
              <OrderParcelEvents
                state={parcelEventsState(
                  consignment.key
                    ? consignmentProgress[consignment.key]
                    : undefined,
                )}
                // The parcel's own sentence stands above where it has one; otherwise the carrier's says its own.
                isHeadlineShown={!consignment.day}
                foldAfter={2}
              />
            )}
          />
        )}
        {tab === "price" && (
          <Card className="gap-3 p-5">
            <PriceBreakdown
              rows={numbered.map(({ item, number }) => ({
                ref: number,
                title: item.description,
                quantity: item.quantity ?? null,
                price_pence: pence(item.net_amount),
                vat_label: null,
              }))}
              totalPence={pence(order.net_amount)}
              totalIncVatPence={hasVat ? pence(order.gross_amount) : null}
            />
            <p className="border-t pt-3 text-sm text-muted-foreground">
              {invoiceState}
              {order.customer_reference
                ? ` · Your reference: ${order.customer_reference}`
                : ""}
            </p>
          </Card>
        )}
        {tab === "documents" && (
          <DocumentsList
            documents={invoices.map((invoice) => ({
              key: invoice.invoice_no,
              kind: invoice.is_credit_note ? "Credit note" : "Invoice",
              label: `${invoice.invoice_no} · ${formatQuotedPrice(pence(invoice.gross_amount))}${invoice.due_on ? `, due ${formatDateOrDash(invoice.due_on)}` : ""}`,
              dated: invoice.invoiced_on,
              // No route serves an invoice PDF to the portal yet (doc 38, question 4).
              url: null,
            }))}
            emptySentence="Nothing has been invoiced for this order yet."
            fetchFile={null}
          />
        )}
        {tab === "notes" && (
          <NotesPanel
            messages={[]}
            questionsHeading="Questions about this order?"
            contact={
              order.contact?.email
                ? { name: order.contact.name, email: order.contact.email }
                : null
            }
            contactLine={`Please quote ${first.job_no ? `job ${first.job_no}` : (order.order_no ?? order.order_ref)} so we can find it straight away.`}
            noContact={
              <p className="text-muted-foreground">
                Call us on{" "}
                <span className="whitespace-nowrap">{POLLARDS_PHONE}</span> and
                we&apos;ll help.
              </p>
            }
          />
        )}
      </div>
    </SummaryPageFrame>
  );
}
